/**
 * Metric presentation — label, number format, which direction is good, and how
 * a change is expressed. The *reduction* (rows → number) lives with whoever
 * owns the rows: the mock engine today, the API tomorrow. Splitting the two is
 * what lets a presenter format a figure it did not compute.
 *
 * Ported from the Reef prototype's `data/metrics.ts`; formats are unchanged.
 */
export type MetricId =
  | 'mentions'
  | 'reach'
  | 'engagement'
  | 'engagementRate'
  | 'nps'
  | 'positiveShare'
  | 'negativeShare'
  | 'negativeMentions'
  | 'positiveMentions'
  | 'netSentiment'
  | 'meanSentiment'
  | 'npsNet'
  | 'shares'
  | 'comments'
  | 'reactions'
  | 'likes';

/** NPS is published on a −10…+10 scale. */
export const NPS_BOUND = 10;

export type MetricMeta = {
  id: MetricId;
  label: string;
  format: (value: number) => string;
  goodDirection: 'up' | 'down';
  /** `points` for figures that are already percentages or scores. Default `percent`. */
  deltaMode?: 'percent' | 'points';
};

export const compactNumber = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
export const wholeNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

const compact = (value: number) => compactNumber.format(value);
const whole = (value: number) => wholeNumber.format(value);
const percent1 = (value: number) => `${value.toFixed(1)}%`;
const signed1 = (value: number) => `${value > 0 ? '+' : ''}${value.toFixed(1)}`;

export const metricMeta: Readonly<Record<MetricId, MetricMeta>> = {
  mentions: { id: 'mentions', label: 'Mentions', format: whole, goodDirection: 'up' },
  reach: { id: 'reach', label: 'Reach', format: compact, goodDirection: 'up' },
  engagement: { id: 'engagement', label: 'Engagement', format: compact, goodDirection: 'up' },
  engagementRate: {
    id: 'engagementRate',
    label: 'Engagement rate',
    format: percent1,
    goodDirection: 'up',
  },
  nps: { id: 'nps', label: 'NPS', format: (v) => v.toFixed(1), goodDirection: 'up' },
  meanSentiment: {
    id: 'meanSentiment',
    label: 'Mean sentiment score',
    format: (v) => v.toFixed(1),
    goodDirection: 'up',
    deltaMode: 'points',
  },
  netSentiment: {
    id: 'netSentiment',
    label: 'Net sentiment',
    format: signed1,
    goodDirection: 'up',
    deltaMode: 'points',
  },
  npsNet: { id: 'npsNet', label: 'NPS', format: signed1, goodDirection: 'up', deltaMode: 'points' },
  positiveShare: {
    id: 'positiveShare',
    label: 'Positive share',
    format: percent1,
    goodDirection: 'up',
  },
  negativeShare: {
    id: 'negativeShare',
    label: 'Negative share',
    format: percent1,
    goodDirection: 'down',
  },
  negativeMentions: {
    id: 'negativeMentions',
    label: 'Negative mentions',
    format: compact,
    goodDirection: 'down',
  },
  positiveMentions: {
    id: 'positiveMentions',
    label: 'Positive mentions',
    format: compact,
    goodDirection: 'up',
  },
  shares: { id: 'shares', label: 'Shares', format: compact, goodDirection: 'up' },
  comments: { id: 'comments', label: 'Comments', format: compact, goodDirection: 'up' },
  reactions: { id: 'reactions', label: 'Reactions', format: compact, goodDirection: 'up' },
  likes: { id: 'likes', label: 'Likes', format: compact, goodDirection: 'up' },
};
